import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { UrlService } from '@services/url.service';
import { CursorPage } from '@shared/components/data-table/paginator';
import { ApiList } from '@shared/models/api-list.model';
import {
  DataDeliveryDelivery,
  DataDeliveryDeliveryExtended,
  DataDeliveryDeliveryStatusColorMap,
} from '@shared/models/data-delivery-delivery.model';
import { DataDeliveryDestination } from '@shared/models/data-delivery-destination.model';
import { SelectListItem } from '@shared/models/select-list-item';
import { chain, get } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class DataDeliveryService {
  private http = inject(HttpClient);
  private urlService = inject(UrlService);

  getDestination(destinationId: string): Observable<DataDeliveryDestination> {
    return this.http.post<DataDeliveryDestination>(this.urlService.DATA_DELIVERY_DESTINATIONS_URL, {
      destination_id: destinationId,
    });
  }

  getDestinations(is835?: boolean): Observable<DataDeliveryDestination[]> {
    const params = new HttpParams({ fromObject: { is_835: !!is835 } });
    return this.http.get<DataDeliveryDestination[]>(this.urlService.DATA_DELIVERY_DESTINATIONS_URL, { params });
  }

  getDestinationsSelectLisItems(is835 = true): Observable<SelectListItem[]> {
    return this.getDestinations(is835).pipe(
      map(destinations => {
        return chain(destinations)
          .sortBy(({ destination_id }) => (destination_id || '').toLocaleLowerCase())
          .map(destination => ({
            name: `${destination.destination_id}${destination.name ? ` (${destination.name})` : ''}`,
            value: destination.destination_id,
          }))
          .value();
      }),
    );
  }

  upsertDestination(model: DataDeliveryDestination): Observable<boolean> {
    return this.http
      .put(this.urlService.DATA_DELIVERY_DESTINATIONS_URL, {
        ...model,
        host: model.server_name,
      })
      .pipe(
        map(result => {
          if (result === 'success' || get(result, 'status') === 'success') {
            return true;
          }
          throw new Error(`Saving destination failed! Error: ${result}`);
        }),
      );
  }

  getDeliveries(filter: Record<string, any>, page?: CursorPage): Observable<ApiList<DataDeliveryDeliveryExtended>> {
    return this.http
      .post<ApiList<DataDeliveryDelivery>>(this.urlService.DATA_DELIVERY_DELIVERIES_URL, filter, {
        params: this.createDeliveriesPaginatorRequestParams(page),
      })
      .pipe(
        map(result => ({
          ...result,
          items: result.items.map(delivery => ({
            ...delivery,
            statusColor: `${DataDeliveryDeliveryStatusColorMap[delivery.status]}`,
            filenames: delivery.filenames.map(name => name.replace(/\/?tmp\//, '')),
          })),
        })),
      );
  }

  private createDeliveriesPaginatorRequestParams(page?: CursorPage): HttpParams {
    let params = new HttpParams({
      fromObject: {
        page_size: page?.pageSize ?? 20,
      },
    });

    if (page?.cursor) {
      params = params.append('last_item', JSON.stringify(page.cursor));
    }

    return params;
  }
}
